import React from 'react';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';

import BillingAddress from '../../../components/V2/BillingAddress';
import PaymentInfoV2 from '../../../components/V2/PaymentInfoV2';
import ContactInfo from '../../../components/V2/ContactInfo';
import SubscriptionItem from './components/SubscriptionItem';
import OrderHistory from './components/OrderHistory';

import { NavigationHeader } from 'onramp/ui/components';
import { Box, Hr, Text, Flex } from 'core/ui/atoms';
import { GET_PROGRAM_DETAIL, GET_BILLING_INFO } from 'core/api/apollo/queries';
import { isLoading } from 'core/api/loadingHelpers';
import Loading from 'core/ui/components/Loading';
import { invoicesPath } from 'onramp/api/navigation';
import ResponsiveView from 'core/ui/components/ResponsiveView';
import Money from 'core/api/Money';
import useUser from 'core/hooks/useUser';
import { ReactComponent as CopyIcon } from 'core/assets/images/V2/icons/CopyIcon.svg';

const MySettingsPage = () => {
  const history = useHistory();
  const { currentUser } = useUser();
  const { loading, refetch, data: { getProgramDetail = {} } = {} } = useQuery(GET_PROGRAM_DETAIL);
  const { data: { getBillingInfo: billingInfo } = {} } = useQuery(GET_BILLING_INFO);

  const goToInvoicePath = () => history.push(invoicesPath());

  const copyReferralCodeToClipboard = () => {
    const code = currentUser?.referralCoupon?.code;
    if (code) {
      navigator.clipboard.writeText(code);
    }
  };

  if (isLoading(loading, getProgramDetail)) {
    return <Loading />;
  }

  return (
    <Box>
      <NavigationHeader title='My Settings' />
      <Hr />
      <Title>My Subscriptions</Title>
      <SubscriptionItem refetch={refetch} item={getProgramDetail} />
      <Hr />
      <ResponsiveView desktop={true}>
        <BillingContainer direction='row' justifyContent='center' gap='40px'>
          <Box>
            <BillingAddress title='Delivery Address' />
            <PaymentInfoV2 title='My Payment Methods' />
            <Heading>Available Credits</Heading>
            <SubTitle>{Money(billingInfo?.availableCredits).toFormat()}</SubTitle>
            <Heading>Referral Code</Heading>
            <Flex direction='row' alignItems='flex-start' gap='10px'>
              <SubTitle>{currentUser?.referralCoupon?.code}</SubTitle>
              <IconContainer onClick={copyReferralCodeToClipboard}>
                <CopyIcon />
              </IconContainer>
            </Flex>
          </Box>
          <DetailsSecondView gap='40px' direction='row' alignItems='center' justifyContent='center'>
            <Title>My Invoices</Title>
            <UnderLineText onClick={goToInvoicePath}>View Invoices</UnderLineText>
          </DetailsSecondView>
        </BillingContainer>
      </ResponsiveView>
      <ResponsiveView desktop={false}>
        <Container>
          <Title>Personal Details</Title>
          <ContactInfo title='' />
        </Container>
        <Hr />
        <Container>
          <Title>My Payments Methods</Title>
          <PaymentInfoV2 title=' ' />
        </Container>
        <Hr />
        <Container>
          <Title>Available Credits</Title>
          <SubTitle>{Money(billingInfo?.availableCredits).toFormat()}</SubTitle>
        </Container>
        <Hr />
        <Container>
          <Title>Referral Code</Title>
          <Flex direction='row' justifyContent='center' alignItems='flex-start' gap='10px'>
            <CopyTitle>{currentUser?.referralCoupon?.code}</CopyTitle>
            <IconContainer onClick={copyReferralCodeToClipboard}>
              <CopyIcon />
            </IconContainer>
          </Flex>
        </Container>
        <Hr />
        <Title>Default Shipping Address</Title>
        <Container>
          <BillingAddress title='' />
        </Container>
        <Hr />
        <Title>My Invoices</Title>
        <Flex direction='row' alignItems='center' justifyContent='center'>
          <UnderLineText onClick={goToInvoicePath}>View Invoices</UnderLineText>
        </Flex>
        <Hr />
        <OrderHistory />
      </ResponsiveView>
    </Box>
  );
};

const IconContainer = styled(Box)`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const BillingContainer = styled(Flex)`
  margin-top: 50px;
`;

const DetailsSecondView = styled(Flex)`
  p {
    margin: 0px;
  }
`;

const Container = styled(Box)`
  padding-left: 10px;
  padding-right: 10px;
`;

const UnderLineText = styled(Text)`
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  color: ${(p) => p.theme.colors.primaryV2};
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  text-decoration: underline;
  text-underline-offset: 5px;
  cursor: pointer;
`;

const Title = styled(Text)`
  font-family: ${({ theme }) => theme.typography.familyLarken};
  color: ${(p) => p.theme.colors.primaryV2};
  text-align: center;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
`;

const Heading = styled(Text)`
  font-family: ${({ theme }) => theme.typography.familyLarken};
  color: ${(p) => p.theme.colors.primaryV2};
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  margin: 0px;
`;

const SubTitle = styled(Text)`
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  color: ${(p) => p.theme.colors.primaryV2};
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  margin-top: 0px;
  margin-bottom: 20px;
`;

const CopyTitle = styled(SubTitle)`
  margin-bottom: 0px;
`;

export default MySettingsPage;
